import { supabase } from '../lib/supabaseClient';
import type { Member } from '../types/member';
import type { FinancialHealthStats } from '../types/financialStats';
import type { DataSource } from '../contexts/DataSourceContext';

export const getMemberData = async (uuid: string): Promise<Member | null> => {
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .eq('uuid', uuid)
    .single();

  if (error) throw error;
  if (import.meta.env.DEV) console.log('Full member data:', data);
  return data;
};

/**
 * ⚠️ RANK 44 — DIRECT SUPABASE RAW READ (BUG-SCORING-PATH-DIVERGENCE-01)
 *
 * Queries financial_health_stats_v2 directly. This table does NOT contain
 * official publication contract fields (score_status, score_source, run_id,
 * officialized_at). The official score read contract CANNOT be enforced here.
 *
 * Active consumers: ActionsPanel.tsx, FinancialStats.tsx
 *
 * Full remediation requires a SQL view that joins with official contract fields.
 * See Phase 9H-SQL proposal in workplan.
 *
 * For lender member list/summary scores, use lenderApiService.ts instead.
 */
export const getFinancialStats = async (
  uuid: string,
  dataSource: DataSource = 'v2'
): Promise<FinancialHealthStats[]> => {
  if (import.meta.env.DEV) console.log(`📊 [RANK44_RAW_READ] Fetching financial stats for ${uuid} from ${dataSource} table — no official contract enforcement`);
  const tableName = dataSource === 'v1' ? 'financial_health_stats' : 'financial_health_stats_v2';

  // Both tables use 'uuid' as the identifier column
  const columnName = 'uuid';

  const { data, error } = await supabase
    .from(tableName)
    .select('*')
    .eq(columnName, uuid)
    .order('month', { ascending: false });

  if (import.meta.env.DEV) console.log(`📊 Query: ${tableName}.${columnName} = ${uuid}, Results: ${data?.length || 0}`);

  if (error) throw error;
  if (import.meta.env.DEV) console.log(`Retrieved ${data?.length || 0} records, most recent month: ${data?.[0]?.month}`);
  
  // Calculate yearly_gross_income for each record
  const enrichedData = data.map((record, _index, array) => {
    // Get the most recent 12 months of gross_income (or as many as available)
    const monthsToConsider = Math.min(12, array.length);
    const recentMonths = array.slice(0, monthsToConsider);
    
    if (import.meta.env.DEV) console.log(`[DEBUG] memberApi: Calculating yearly_gross_income for month ${record.month}`);
    if (import.meta.env.DEV) console.log(`[DEBUG] memberApi: Number of months considered: ${recentMonths.length}`);
    
    // Sum up the gross_income values for those months
    const yearlyGrossIncome = recentMonths.reduce((sum, month) => 
      sum + (month.gross_income || 0), 0);
    
    if (import.meta.env.DEV) console.log(`[DEBUG] memberApi: Calculated yearly_gross_income: ${yearlyGrossIncome}`);
    
    return {
      ...record,
      yearly_gross_income: yearlyGrossIncome
    };
  });
  
  return enrichedData;
};

// Use the environment variable directly for the URL
export const API_ENDPOINTS = {
  members: `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/members`,
  financialStats: `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/financial_health_stats`,
};

// Add new function to get member list with pagination support
export const getAllMembers = async (options?: {
  page?: number;
  pageSize?: number;
  fetchAll?: boolean;
}): Promise<{ data: Member[]; total: number }> => {
  const { page = 1, pageSize = 20, fetchAll = false } = options || {};

  let query = supabase.from('members').select('*', { count: 'exact' });

  // If not fetching all, apply pagination
  if (!fetchAll) {
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;
    query = query.range(start, end);
  }

  // Always order by member_name for consistency
  query = query.order('member_name');

  const { data, error, count } = await query;

  if (error) throw error;
  return {
    data: data as Member[],
    total: count || 0,
  };
};

// Add function to get filtered members from the server
export const getFilteredMembers = async (filters: {
  search?: string;
  status?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  showLcuOnly?: boolean;
  orgId?: string;  // Dynamic org_id from logged-in user
  page?: number;
  pageSize?: number;
}): Promise<{ data: Member[]; total: number }> => {
  if (import.meta.env.DEV) console.log('🔍 getFilteredMembers called with filters:', filters);

  const {
    search = '',
    status = [],
    sortBy = 'member_name',
    sortOrder = 'asc',
    showLcuOnly = false,
    orgId,
    page = 1,
    pageSize = 20,
  } = filters;

  // If showLcuOnly is true, first get member_ids from member_orgs who have consented
  let consentedMemberIds: string[] | null = null;
  if (showLcuOnly) {
    // Require org_id when filtering by organization
    if (!orgId) {
      if (import.meta.env.DEV) console.warn('⚠️ showLcuOnly=true but no orgId provided, returning empty result');
      return { data: [], total: 0 };
    }

    if (import.meta.env.DEV) console.log('👥 Fetching consented members from member_orgs for org:', orgId);
    const { data: memberOrgs, error: memberOrgsError } = await supabase
      .from('member_orgs')
      .select('member_id')
      .eq('org_id', orgId)
      .eq('status', 'active');

    if (memberOrgsError) {
      if (import.meta.env.DEV) console.error('❌ Error fetching member_orgs:', memberOrgsError);
      throw memberOrgsError;
    }

    consentedMemberIds = memberOrgs?.map(mo => mo.member_id) || [];
    if (import.meta.env.DEV) console.log('✅ Found consented member_ids:', consentedMemberIds.length, consentedMemberIds);

    // If no consented members, return empty result
    if (consentedMemberIds.length === 0) {
      if (import.meta.env.DEV) console.log('⚠️ No consented members found, returning empty result');
      return { data: [], total: 0 };
    }
  }

  let query = supabase.from('members').select('*', { count: 'exact' });
  if (import.meta.env.DEV) console.log('📊 Initial query created');

  // Filter by consented member_ids if showLcuOnly
  if (showLcuOnly && consentedMemberIds && consentedMemberIds.length > 0) {
    if (import.meta.env.DEV) console.log('🔒 Filtering members by consented member_ids');
    query = query.in('uuid', consentedMemberIds);
  }

  // Helper function to escape special characters in search terms
  const escapeSearchTerm = (term: string): string => {
    // Escape characters that have special meaning in SQL LIKE patterns
    // This is critical for preventing SQL injection and syntax errors
    return term.replace(/[%_\\]/g, '\\$&');
  };

  // Apply filters
  if (search) {
    if (import.meta.env.DEV) console.log('🔎 Applying search filters for:', search);
    // Split search term into words for better multi-word searching
    const searchTerms = search
      .trim()
      .split(/\s+/)
      .filter(term => term.length > 0);

    // Build a simple array of filter conditions
    const filterConditions: string[] = [];

    // Always add a condition for the full search term
    const fullSearchTerm = escapeSearchTerm(search);
    filterConditions.push(`member_name.ilike.%${fullSearchTerm}%`);

    // For multi-word searches (e.g., "Thomas Brown")
    if (searchTerms.length > 1) {
      // First word is likely a first name
      const firstWord = escapeSearchTerm(searchTerms[0]);
      filterConditions.push(`first_name.ilike.%${firstWord}%`);

      // Last word is likely a last name
      const lastWord = escapeSearchTerm(searchTerms[searchTerms.length - 1]);
      filterConditions.push(`last_name.ilike.%${lastWord}%`);

      // Also add conditions for each word individually
      searchTerms.forEach(term => {
        const escapedTerm = escapeSearchTerm(term);
        filterConditions.push(`member_name.ilike.%${escapedTerm}%`);
      });
    } else {
      // Single word search - check all fields
      const term = escapeSearchTerm(searchTerms[0]);
      filterConditions.push(`first_name.ilike.%${term}%`);
      filterConditions.push(`last_name.ilike.%${term}%`);
      filterConditions.push(`email.ilike.%${term}%`);
    }

    // Apply the OR conditions
    query = query.or(filterConditions.join(','));
    if (import.meta.env.DEV) console.log('🔍 Search conditions applied:', filterConditions);
  }

  if (status.length > 0) {
    if (import.meta.env.DEV) console.log('📋 Applying status filters:', status);
    query = query.in('dashboard_status', status);
  }

  // Note: showLcuOnly filtering is now done via member_orgs JOIN at the top of this function

  // Apply sorting - handle verascore specially since it's not a database column
  if (sortBy === 'verascore') {
    if (import.meta.env.DEV) console.log('📊 Sorting by verascore (reverting to client-side sorting)');
    
    // For verascore sorting, we'll use the client-side approach
    // Use a consistent ordering for the base data
    query = query.order('last_name', { ascending: true });
    query = query.order('first_name', { ascending: true });
  } else if (sortBy === 'email') {
    if (import.meta.env.DEV) console.log('📊 Sorting by email (natural sort will be handled client-side)');
    // When sorting by email, use a basic ordering on the server
    // The natural order sorting for emails will be done client-side in MemberListPage.tsx
    query = query.order('email', { ascending: sortOrder === 'asc' });
  } else {
    // Map 'name' or 'member_name' to last_name, first_name sorting
    if (sortBy === 'name' || sortBy === 'member_name') {
      if (import.meta.env.DEV) console.log('📊 Sorting by last_name and first_name');
      // First sort by whether the name contains non-English characters
      // This puts English names first and non-English names at the bottom
      // Then sort by last_name, then by first_name
      query = query
        .order('last_name', { ascending: sortOrder === 'asc' })
        .order('first_name', { ascending: sortOrder === 'asc' });
    } else {
      if (import.meta.env.DEV) console.log('📊 Sorting by:', sortBy, 'order:', sortOrder);
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });
    }
  }

  // Apply pagination
  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;
  query = query.range(start, end);
  if (import.meta.env.DEV) console.log('📄 Applying pagination:', { start, end });

  try {
    if (import.meta.env.DEV) console.log('🚀 Executing query...');
    const { data, error, count } = await query;

    if (error) {
      if (import.meta.env.DEV) console.error('❌ Query error:', error);
      throw error;
    }

    if (import.meta.env.DEV) console.log('✅ Query successful:', {
      resultCount: data?.length || 0,
      totalCount: count || 0 
    });

    return {
      data: data as Member[],
      total: count || 0,
    };
  } catch (error) {
    if (import.meta.env.DEV) console.error('❌ Error in getFilteredMembers:', error);
    throw error;
  }
};

export const getMemberByEmail = async (email: string): Promise<Member | null> => {
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .eq('email', email)
    .single();

  if (error) throw error;
  return data;
};

export const updateMemberStatus = async (uuid: string, status: string): Promise<Member> => {
  const { data, error } = await supabase
    .from('members')
    .update({ dashboard_status: status })
    .eq('uuid', uuid)
    .select()
    .single();

  if (error) throw error;
  return data as Member;
};

export const updateMember = async (uuid: string, updates: Partial<Member>): Promise<Member> => {
  if (import.meta.env.DEV) console.log('[updateMember] Updating member:', uuid, 'with:', updates);
  
  const { data, error } = await supabase
    .from('members')
    .update(updates)
    .eq('uuid', uuid)
    .select();

  if (error) {
    if (import.meta.env.DEV) console.error('[updateMember] Error:', error);
    throw error;
  }
  
  if (!data || data.length === 0) {
    if (import.meta.env.DEV) console.error('[updateMember] No member found with uuid:', uuid);
    throw new Error(`No member found with uuid: ${uuid}`);
  }
  
  if (import.meta.env.DEV) console.log('[updateMember] Success:', data[0]);
  return data[0] as Member;
};
